import { ListItem, QueryList } from "construct-ui";
import { v4 as uuid } from "uuid";
import languageModel from "../../models/language.model";

/**
 * Query List Component
 * 
 * Component that helps to display a list of items.
 * Supports infinite scrolling to constantly query results when hits the end of scrollbar.
 * 
 * @author Darrus Goh
 */
export default function QueryListComponent() {
  const DEFAULT_ONSELECT = () => { console.log('QueryListComponent: onSelect(selectedItems)') };
  const SCROLL_END_TOLERANCE_PERCENTAGE = 0.9;
  const LIST_ID = uuid();

  let listElement = null;
  let oldScrollTop = 0;
  let oldScrollHeight = 0;
  let isFiltering = false;

  // COMMON FUNCTIONS
  const itemPredicate = (itemValue) => (query, item) => {
    isFiltering = query !== '';
    return item[itemValue]?.toLowerCase().includes(query.toLowerCase());
  };


  // SINGLE SELECT FUNCTIONS
  const itemRenderSingle = (selectedItem, itemKey, itemValue) => (item) => {
    return m(ListItem, {
      allowOnContentClick: true,
      label: item[itemValue],
      selected: JSON.stringify(selectedItem) === JSON.stringify(item)
    });
  };

  const getItemListSingle = (items, selectedItem, itemKey, itemValue) => {
    let result = [];

    if (items && items.length > 0) {
      result = structuredClone(items);
    }

    // item list does not have item
    if (selectedItem && !result.find((item) => item[itemKey] === selectedItem[itemKey])) {
      result = [selectedItem].concat(result);
    }

    return result;
  };

  const handleSelectSingle = (items, selectedItem, onSelect, itemKey, itemValue) => (item) => {
    selectedItem = JSON.stringify(selectedItem) === JSON.stringify(item) ? null : item;
    onSelect(selectedItem);
  };


  // MULTI SELECT FUNCTIONS 
  const itemRenderMulti = (selectedItems, itemKey, itemValue) => (item) => {
    return m(ListItem, {
      allowOnContentClick: true,
      label: item[itemValue],
      selected: selectedItems.find((selectedItem) => selectedItem[itemKey] === item[itemKey]),
    });
  };

  const getItemListMulti = (items, selectedItems, itemKey, itemValue) => {
    let result = [];

    if (items && items.length > 0) {
      result = structuredClone(items);
    }

    // Find items not in main list
    let selectedItemsNotFound = selectedItems.filter((selectedItem) => !items.find((item) => item[itemKey] === selectedItem[itemKey]));
    result = selectedItemsNotFound.concat(result);

    if (result.length > 1) {
      if (result.length !== selectedItems.length) {
        result = [{ [itemKey]: '', [itemValue]: languageModel.getText('LT00000077', "Select all") }].concat(result);
      } else {
        result = [{ [itemKey]: '', [itemValue]: languageModel.getText('LT00000285', "Clear selection") }].concat(result);
      }
    }

    return result;
  };

  const handleSelectMulti = (items, selectedItems, onSelect, itemKey, itemValue, max=-1) => (item) => {
    let result = structuredClone(selectedItems);
    let errorMsg;
    let unionArr = new Set([]);
    const checkExceed = ()=> max != -1 && unionArr.size > max;

    if (item[itemValue] === languageModel.getText('LT00000077', "Select all")) {
      unionArr = new Set([...items,...result]);
      if(checkExceed()){
        errorMsg = `Failed: Maximum ${max} items.`
      }else{
        result = structuredClone(items);
      }
      
    } else if (item[itemValue] === languageModel.getText('LT00000285', "Clear selection")) {
      result = [];
    } else {
      let index = result.indexOf(result.find((element) => item[itemKey] === element[itemKey]));
      unionArr = new Set([...result, item]);
      if (index < 0) {
        if(checkExceed()){
          
          errorMsg = `Failed: Maximum ${max} items.`
        }else{
          result.push(item);
        }
        
      } else {
        result.splice(index, 1);
      }
    }

    onSelect(result, errorMsg);
  };

  // Placed here because the functions above are variables so the order of declaration matters
  const itemRenderFunctions = [itemRenderSingle, itemRenderMulti];
  const getItemListFunctions = [getItemListSingle, getItemListMulti];
  const handleSelectFunctions = [handleSelectSingle, handleSelectMulti];

  const DEBOUNCE_TIME = 200;
  const handleInfiniteScroll = (onInfiniteScroll, listElement) => _.debounce(() => {
    oldScrollTop = listElement.scrollTop;
    if (!isFiltering && (listElement.scrollHeight - listElement.scrollTop) * SCROLL_END_TOLERANCE_PERCENTAGE < listElement.offsetHeight) {
      onInfiniteScroll();
    }
  }, DEBOUNCE_TIME);

  return {
    oncreate: ({ attrs: { onInfiniteScroll = null } }) => {
      if (onInfiniteScroll) {
        listElement = document.getElementById(LIST_ID);
        listElement.addEventListener('scroll', handleInfiniteScroll(onInfiniteScroll, listElement));
      }
    },
    onupdate: () => {
      if (listElement) {
        if (listElement.scrollHeight >= oldScrollHeight) {
          listElement.scrollTop = oldScrollTop;
        } else {
          listElement.scrollTop = 0;
        }
        oldScrollHeight = listElement.scrollHeight;
      }
    },
    view: ({
      attrs: {
        filterable = true,
        multiple = false,
        items = [],
        selectedItem = null,
        selectedItems = null,
        onSelect = DEFAULT_ONSELECT,
        style = '',
        itemKey = 'code',
        itemValue = 'name',
        listAttrs = {},
        max = -1
      }
    }) => {
      const selected = multiple ? selectedItems : selectedItem;

      return m(QueryList, {
        class: 'd-flex flex-column h-100', // so that the list will fit within a height constraint
        filterable,
        items: getItemListFunctions[+multiple](items, selected, itemKey, itemValue),
        onSelect: handleSelectFunctions[+multiple](items, selected, onSelect, itemKey, itemValue, max),
        itemRender: itemRenderFunctions[+multiple](selected, itemKey, itemValue),
        itemPredicate: itemPredicate(itemValue),
        listAttrs: Object.assign({
          id: LIST_ID,
          class: 'flex-fill',
          style: { 'max-height': 'none' }
        }, listAttrs),
        style
      });
    }
  }
}
